import React from "react";

interface TypingIndicatorProps {
  /** Color of the dots - defaults to gray */
  color?: string;
  /** Size of the dots - sm, md, lg */
  size?: "sm" | "md" | "lg";
  /** Container background color */
  backgroundColor?: string;
  /** Whether to show as a chat bubble */
  asChatBubble?: boolean;
  /** Custom container className */
  className?: string;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  color = "#9CA3AF", // gray-400
  size = "md",
  backgroundColor = "#F3F4F6", // gray-100
  asChatBubble = true,
  className = "",
}) => {
  // Size configurations with proper spacing for bounce animation
  const sizeConfig = {
    sm: {
      dotSize: 6,
      spacing: 4,
      containerPadding: 12,
      minHeight: 40,
      verticalPadding: 8,
    },
    md: {
      dotSize: 8,
      spacing: 6,
      containerPadding: 16,
      minHeight: 56,
      verticalPadding: 12,
    },
    lg: {
      dotSize: 10,
      spacing: 8,
      containerPadding: 20,
      minHeight: 64,
      verticalPadding: 16,
    },
  };

  const config = sizeConfig[size];

  // Dot component with proper animation
  const Dot: React.FC<{ delay: number }> = ({ delay }) => (
    <div
      className="animate-bounce"
      style={{
        width: config.dotSize,
        height: config.dotSize,
        backgroundColor: color,
        borderRadius: "50%",
        animationDelay: `${delay}ms`,
        animationDuration: "1s",
        animationIterationCount: "infinite",
      }}
    />
  );

  // Dots container with proper spacing and accessibility
  const DotsContainer = () => (
    <div
      className="flex items-center justify-center"
      role="status"
      aria-label="Typing"
      aria-live="polite"
      style={{
        gap: config.spacing,
        height: config.dotSize + config.verticalPadding, // Space for bounce
        overflow: "visible", // Ensure animation isn't clipped
      }}
    >
      <Dot delay={0} />
      <Dot delay={160} />
      <Dot delay={320} />
    </div>
  );

  if (!asChatBubble) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <DotsContainer />
      </div>
    );
  }

  return (
    <div
      className={`flex items-start ${className}`}
      style={{ marginBottom: 16, overflow: "visible" }}
    >
      <div
        style={{
          backgroundColor,
          borderRadius: 7,
          maxWidth: "80%",
          minHeight: config.minHeight,
          padding: `${config.verticalPadding}px ${config.containerPadding}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible", // Critical: don't clip the animation
        }}
      >
        <DotsContainer />
      </div>
    </div>
  );
};

export { TypingIndicator };
